// Third Party
import * as React from 'react';

// Utils
import { useNotification } from './use-notification';
import { useToggle } from '@hooks';

// UI
import { LoginModal } from '@components/login-modal';
import { SignupModal } from '@components/signup-modal';

export type LoginAndSignupModalsContextValue = {
  activateLoginModal: VoidFunction;
  deactivateLoginModal: VoidFunction;
  loginModalActive: boolean;
  activateSignupModal: VoidFunction;
  deactivateSignupModal: VoidFunction;
  signupModalActive: boolean;
  showUnauthenticatedError: (options?: { consoleMessage?: string }) => void;
  showGenericError: (options?: {
    consoleMessage?: string;
    timeout: number;
  }) => void;
};

interface Props {
  children: React.ReactNode;
}

export const LoginAndSignupModalsProvider: React.FC<Props> = ({ children }) => {
  const { addNotification } = useNotification();

  const {
    on: loginModalActive,
    setOn: activateLoginModal,
    setOff: deactivateLoginModal,
  } = useToggle(false);

  const {
    on: signupModalActive,
    setOn: activateSignupModal,
    setOff: deactivateSignupModal,
  } = useToggle(false);

  const contextValue: LoginAndSignupModalsContextValue = {
    activateLoginModal,
    deactivateLoginModal,
    loginModalActive,
    activateSignupModal,
    deactivateSignupModal,
    signupModalActive,
    showUnauthenticatedError: () => {
      addNotification({
        type: 'error',
        title: 'error',
        message: 'User is unauthenticated',
      });
    },
    showGenericError: (options?: {
      consoleMessage?: string;
      timeout: number;
    }) => {
      addNotification({
        type: 'error',
        title: 'error',
        timeout: options?.timeout,
        message: 'An error occurred',
      });
    },
  };

  return (
    <LoginAndSignupModalsContext.Provider value={contextValue}>
      <LoginModal />
      <SignupModal />
      {children}
    </LoginAndSignupModalsContext.Provider>
  );
};

export const LoginAndSignupModalsContext = React.createContext<
  LoginAndSignupModalsContextValue | undefined
>(undefined);

export const useLoginAndSignupModals = () => {
  const context = React.useContext(LoginAndSignupModalsContext);

  if (context === undefined) {
    throw new Error(
      'useLoginAndSignupModals must be used within a LoginAndSignupModalsProvider'
    );
  }

  return context;
};
